import { useEffect, useRef } from "react";

// Animated floating shapes with CSS
function FloatingShape({ style, delay = 0 }) {
  return (
    <div
      className="absolute rounded-full opacity-40 animate-float"
      style={{
        ...style,
        animationDelay: `${delay}s`,
      }}
    />
  );
}

// Interactive particle that follows mouse
function InteractiveParticle({ x, y, size, color, delay }) {
  return (
    <div
      className="absolute rounded-full animate-pulse-glow"
      style={{
        left: `${x}%`,
        top: `${y}%`,
        width: size,
        height: size,
        backgroundColor: color,
        animationDelay: `${delay}s`,
        filter: `blur(${parseInt(size) / 4}px)`,
      }}
    />
  );
}

export default function Background3D() {
  const containerRef = useRef(null);
  const mouseRef = useRef({ x: 50, y: 50 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        mouseRef.current = {
          x: ((e.clientX - rect.left) / rect.width) * 100,
          y: ((e.clientY - rect.top) / rect.height) * 100,
        };
        
        // Move shapes based on mouse position
        const shapes = containerRef.current.querySelectorAll('.mouse-reactive');
        shapes.forEach((shape, index) => {
          const speed = 0.02 + (index * 0.005);
          const offsetX = (mouseRef.current.x - 50) * speed;
          const offsetY = (mouseRef.current.y - 50) * speed;
          shape.style.transform = `translate(${offsetX}px, ${offsetY}px)`;
        });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const shapes = [
    // Large warm spheres
    { x: -5, y: 10, size: 300, color: 'rgba(224, 90, 71, 0.15)', blur: 60 },
    { x: 80, y: 60, size: 400, color: 'rgba(232, 167, 83, 0.12)', blur: 80 },
    { x: 60, y: -10, size: 250, color: 'rgba(140, 99, 72, 0.1)', blur: 50 },
    { x: -10, y: 70, size: 350, color: 'rgba(200, 73, 56, 0.1)', blur: 70 },
    { x: 90, y: 20, size: 200, color: 'rgba(224, 90, 71, 0.12)', blur: 40 },
  ];

  const particles = [
    { x: 10, y: 20, size: '8px', color: 'rgba(224, 90, 71, 0.6)', delay: 0 },
    { x: 85, y: 15, size: '6px', color: 'rgba(232, 167, 83, 0.5)', delay: 0.5 },
    { x: 25, y: 80, size: '10px', color: 'rgba(140, 99, 72, 0.4)', delay: 1 },
    { x: 70, y: 75, size: '7px', color: 'rgba(224, 90, 71, 0.5)', delay: 1.5 },
    { x: 50, y: 30, size: '5px', color: 'rgba(200, 73, 56, 0.6)', delay: 2 },
    { x: 15, y: 50, size: '9px', color: 'rgba(232, 167, 83, 0.4)', delay: 2.5 },
    { x: 90, y: 45, size: '6px', color: 'rgba(224, 90, 71, 0.5)', delay: 3 },
    { x: 40, y: 85, size: '8px', color: 'rgba(140, 99, 72, 0.5)', delay: 3.5 },
    { x: 60, y: 10, size: '7px', color: 'rgba(200, 73, 56, 0.4)', delay: 4 },
    { x: 5, y: 35, size: '5px', color: 'rgba(232, 167, 83, 0.6)', delay: 4.5 },
  ];

  return (
    <div 
      ref={containerRef}
      className="fixed inset-0 z-0 overflow-hidden pointer-events-none"
      style={{ perspective: '1000px' }}
    >
      {/* Large floating gradient spheres */}
      {shapes.map((shape, index) => (
        <div
          key={index}
          className="absolute rounded-full mouse-reactive transition-transform duration-300 ease-out animate-float-slow"
          style={{
            left: `${shape.x}%`,
            top: `${shape.y}%`,
            width: shape.size,
            height: shape.size,
            background: `radial-gradient(circle, ${shape.color} 0%, transparent 70%)`,
            filter: `blur(${shape.blur}px)`,
            animationDelay: `${index * 0.5}s`,
            animationDuration: `${15 + index * 2}s`,
          }}
        />
      ))}

      {/* Small glowing particles */}
      {particles.map((particle, index) => (
        <InteractiveParticle key={index} {...particle} />
      ))}

      {/* Animated rings */}
      <div 
        className="absolute mouse-reactive animate-spin-slow"
        style={{
          left: '15%',
          top: '25%',
          width: 150,
          height: 150,
          border: '2px solid rgba(224, 90, 71, 0.2)',
          borderRadius: '50%',
          animationDuration: '20s',
        }}
      />
      <div 
        className="absolute mouse-reactive animate-spin-slow"
        style={{
          right: '20%',
          bottom: '30%',
          width: 100,
          height: 100,
          border: '2px solid rgba(232, 167, 83, 0.15)',
          borderRadius: '50%',
          animationDuration: '25s',
          animationDirection: 'reverse',
        }}
      />
      <div 
        className="absolute mouse-reactive animate-spin-slow"
        style={{
          left: '60%',
          top: '10%',
          width: 80,
          height: 80,
          border: '2px solid rgba(140, 99, 72, 0.2)',
          borderRadius: '50%',
          animationDuration: '18s',
        }}
      />

      {/* Subtle grid pattern */}
      <div 
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(224, 90, 71, 0.5) 1px, transparent 1px),
            linear-gradient(90deg, rgba(224, 90, 71, 0.5) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
        }}
      />

      <style>{`
        @keyframes float-slow {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-30px) rotate(5deg);
          }
        }
        
        @keyframes pulse-glow {
          0%, 100% {
            opacity: 0.4;
            transform: scale(1);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.2);
          }
        }
        
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        .animate-float-slow {
          animation: float-slow 15s ease-in-out infinite;
        }
        
        .animate-pulse-glow {
          animation: pulse-glow 3s ease-in-out infinite;
        }
        
        .animate-spin-slow {
          animation: spin-slow 20s linear infinite;
        }
      `}</style>
    </div>
  );
}
